import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ScrollSection } from '../components/ScrollSection';

function SymptomRelated() {
  const [searchParams] = useSearchParams();
  const name = searchParams.get('name') || '';
  const [loading, setLoading] = useState(true);
  const [symptom, setSymptom] = useState('');
  const [diseases, setDiseases] = useState([]);
  const [description, setDescription] = useState(null);
  const [excluded, setExcluded] = useState(false);
  const [tghNews, setTghNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setNewsLoading(true);
        const response = await axios.get('/api/news');
        const news = Array.isArray(response.data)
          ? response.data
          : response.data?.news || [];
        news.sort((a, b) => new Date(b.date) - new Date(a.date));
        if (!cancelled) setTghNews(news);
      } catch (e) {
        console.error('Error fetching news:', e);
        if (!cancelled) setTghNews([]);
      } finally {
        if (!cancelled) setNewsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  useEffect(() => {
    if (!name.trim()) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `/api/symptoms/from-diseases/related?name=${encodeURIComponent(name)}`
        );
        if (cancelled) return;
        setSymptom(res.data.symptom || name);
        setDiseases(res.data.diseases || []);
        setDescription(res.data.description || null);
        setExcluded(Boolean(res.data.excluded));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setSymptom(name);
          setDiseases([]);
          setDescription(null);
          setExcluded(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [name]);

  return (
    <div id="symptom-related-page">
      <section className="py-4" style={{ backgroundColor: '#fff', color: '#333' }}>
        <div className="container">
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb" style={{ backgroundColor: 'transparent', padding: 0, margin: 0 }}>
              <li className="breadcrumb-item"><Link to="/" style={{ color: 'var(--tgh-blue-light)', textDecoration: 'none' }}>Home</Link></li>
              <li className="breadcrumb-item"><Link to="/symptoms" style={{ color: 'var(--tgh-blue-light)', textDecoration: 'none' }}>Symptoms</Link></li>
              <li className="breadcrumb-item active" style={{ color: '#666' }} aria-current="page">
                Related conditions
              </li>
            </ol>
          </nav>

          <div className="mb-2">
            <span style={{ fontSize: '0.9rem', color: 'var(--tgh-blue-light)', fontWeight: '500' }}>Symptoms</span>
          </div>
          <h1 className="mb-3" style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', fontSize: '2rem', color: 'var(--tgh-blue-light)' }}>
            {name.trim() ? symptom : 'Symptom'}
          </h1>
          {description && (
            <div
              className="mb-3"
              style={{ fontSize: '1rem', lineHeight: '1.65', color: '#444', whiteSpace: 'pre-line' }}
            >
              {description}
            </div>
          )}
          <p className="tgh-diseases-disclaimer mb-3" role="note">
            This is for informational purposes only. For medical advice or a diagnosis, consult a professional or
            healthcare provider.
          </p>
        </div>
      </section>

      <section className="py-4" style={{ backgroundColor: '#fff' }}>
        <div className="container">
          <div className="row align-items-start">
            <div className="col-lg-8">
              <ScrollSection>
                {!name.trim() ? (
                  <p className="text-muted">No symptom selected. <Link to="/symptoms">Browse symptoms</Link>.</p>
                ) : loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : excluded ? (
                  <>
                    <div className="alert alert-secondary">
                      This phrase has been removed from the public symptom browse list. You can still read about conditions
                      under{' '}
                      <Link to="/diseases" style={{ color: 'var(--tgh-blue-light)' }}>
                        Diseases &amp; conditions
                      </Link>
                      .
                    </div>
                    <Link to="/symptoms" className="btn btn-link ps-0" style={{ color: 'var(--tgh-blue-light)' }}>
                      ← Back to symptoms
                    </Link>
                  </>
                ) : (
                  <>
                    <p className="mb-4" style={{ fontSize: '1rem', lineHeight: '1.6', color: '#333' }}>
                      These diseases and conditions include this symptom (or a closely matching phrase) in their symptom
                      information.
                    </p>

                    {diseases.length === 0 ? (
                      <div className="alert alert-info">
                        No matching articles yet. Content editors can add this symptom to relevant disease pages under
                        Symptoms.
                      </div>
                    ) : (
                      <ul className="list-unstyled">
                        {diseases.map((d) => (
                          <li key={d.id} className="mb-3 pb-3 border-bottom">
                            <Link
                              to={`/diseases/detail/${d.id}`}
                              className="text-decoration-none"
                              style={{ color: 'var(--tgh-blue-light)', fontWeight: '600', fontSize: '1.05rem' }}
                            >
                              {d.alias ? `${d.alias} (${d.name})` : d.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="mt-4">
                      <Link to="/symptoms" className="btn btn-link ps-0" style={{ color: 'var(--tgh-blue-light)' }}>
                        ← Back to symptoms
                      </Link>
                    </div>
                  </>
                )}
              </ScrollSection>
            </div>
            <div className="col-lg-4 mt-4 mt-lg-0">
              <div className="border-start ps-lg-4" style={{ borderColor: '#e0e0e0' }}>
                <h6 className="mb-2">TGH News</h6>
                <p className="text-muted small mb-3">
                  Latest news and updates from Tema General Hospital.
                </p>
                {newsLoading ? (
                  <div className="text-center py-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <div className="small text-muted mt-2">Loading news...</div>
                  </div>
                ) : tghNews.length === 0 ? (
                  <p className="text-muted small mb-0">No news articles available at this time.</p>
                ) : (
                  <ul className="list-unstyled small" style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.25rem' }}>
                    {tghNews.map((post) => (
                      <li key={post.id} className="mb-3 pb-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <Link
                          to={`/news/${post.id}`}
                          style={{ color: 'var(--tgh-blue-light)', textDecoration: 'none', fontWeight: 600 }}
                        >
                          {post.title}
                        </Link>
                        {post.date && (
                          <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.15rem' }}>
                            {formatDate(post.date)}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SymptomRelated;
